import React, { Component, createRef } from 'react';
import {
    timeFormatDefaultLocale, select, pointer, line, area, scaleLinear, axisLeft, axisRight, scaleTime, axisBottom, brushX, timeFormat, curveStepAfter, scalePoint, curveStepBefore,
    timeYear, timeMonth, timeWeek, timeDay, timeHour, timeMinute, timeSecond
} from 'd3';
import { DateUnits, InvalidPointTypes, ISeries, ITrendPoint, ITrendXAxis,ITrendYAxis, OnDateRangeChange, ITrendMarker, TooltipPosition } from './Trend.types';
import { getDateDiffUnit, getIndexByValue, isNullOrUndefined } from './Trend.utils';
interface ITrendGraphProps {
    xAxis: ITrendXAxis;
    yAxis: ITrendYAxis;
    isMultipleYAxis: boolean;
    series: ISeries[];
    syncTime: boolean;
    height: number;
    dateUnit: DateUnits;
    dateUnitCount: number;
    showHorizontalGridLines: boolean;
    showVerticalGridLines: boolean;
    gridLineColor: string;
    gridLineWidth: number;
    chartType: string;
    yAxisTicksCount: number,
    xAxisTicksCount: number,
    backgroundImage: string | any;
    backgroundColor: string;
    markerStyle: ITrendMarker;
    xAxisLabelFormat: string;
    onDateRangeChange: OnDateRangeChange;
    invalidStatuses?: string[];
    invalidPointLineType: InvalidPointTypes;
    tooltipPosition: TooltipPosition;
    width: string;
    yAxisWidth: number;
    tooltipFormat: string;
    d3Locale: any;
    showCommonXLabel: boolean;
    showFutureValue: boolean;
    showTooltipOnPos: boolean;
    showDataLimitTitle: boolean;
    customizedMultipleYAxis: boolean;
    removeDataLimitsFromTooltip: boolean;
}
export class TrendGraph extends Component<ITrendGraphProps> {
    // Internal Private properties
    svgRef: any;
    xAxisRef: any;
    yAxisRef: any;
    gridLinesXRef: any;
    gridLinesYRef: any;
    futureGridLine: any;
    shapeRef: any;
    tooltipRef: any;
    brushRef: any;
    tooltipPointerRef: any;
    seriesRef: any;
    dataLimitRef: any;
    gradientRef: any;
    clipPathRef: any;
    margin: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
    width: number;
    calcHeight: number;
    xScale: any;
    yScales: any;
    brush: any;
    isOnZooming?: boolean;
    zoomStart?: Date | number;
    zoomEnd?: Date | number;
    zoomDateUnit?: DateUnits;
    zoomDateUnitCount?: number;
    barWidth?: number;
    dateFormatter?: (date: Date) => string;
    clipPathId?: string;
    xPos: number;
    yPos: number;
    show: boolean;
    indexes: any;
    groupedByProperty: any = [];
    finalSeriesList: any[] = [];  
    constructor(props: ITrendGraphProps) {
        super(props);
        this.svgRef = createRef();
        this.xAxisRef = createRef();
        this.yAxisRef = createRef();
        this.gridLinesXRef = createRef();
        this.gridLinesYRef = createRef();
        this.futureGridLine = createRef();
        this.dataLimitRef = createRef();
        this.shapeRef = createRef();
        this.seriesRef = createRef();
        this.tooltipRef = createRef();
        this.brushRef = createRef();
        this.tooltipPointerRef = createRef();
        this.gradientRef = createRef();
        this.clipPathRef = createRef();
        this.xScale = scaleTime();
        this.calcHeight = 0;
        this.margin = {
            top: 20,
            left: 20,
            right: 40,
            bottom: props.xAxis.height
        };
        this.xPos = 0;
        this.yPos = 0;
        this.show = false;
        this.indexes = [];
        this.setDefaultTimeLocale();
        this.clipPathId = 'trendClipPath' + this.getClipPathId();
    }
    getClipPathId = (): number => {
        // debugger
        //@ts-ignore
        if (window.trendClipPathId) {
            //@ts-ignore
            window.trendClipPathId += 1;
        } else {
            //@ts-ignore
            window.trendClipPathId = 1;
        }
        //@ts-ignore
        return window.trendClipPathId;;
    }
    /**
     * Add gradient definition in svg
     * @param color
     * @param series
     */
    addGradientDef = (series: ISeries) => {
        const gradient = `<linearGradient id=${'gradient' + series.id} x1='0%' y1='0%' x2='0%' y2='100%'>
        <stop offset='0%' style='stop-color:${series.color};stop-opacity:0.1' />
        <stop offset='100%' style='stop-color:${series.color};stop-opacity:0.8' />
    </linearGradient>`;
        const html = this.gradientRef.current.innerHTML + gradient;
        select(this.gradientRef.current).html(html);
        return ('gradient' + series.id);
    }
    /**
    * Add clip part properties to required elements in svg to hide overflown content
    */
    addRemoveClipPath = (isAdd: boolean) => {
        const clipPath = isAdd ? `url(#${this.clipPathId})` : '';
        select(this.seriesRef.current).attr('clip-path', clipPath);
        select(this.dataLimitRef.current).attr('clip-path', clipPath);
        select(this.shapeRef.current).attr('clip-path', clipPath);
        select(this.tooltipPointerRef.current).attr('clip-path', clipPath);
    }
    bindEvents = () => {
        const proxy = this;
        const chartType = proxy.props.chartType;
        // To show tooltip
        let ref = chartType === "Bar" ? this.seriesRef.current : this.svgRef.current;
        select(chartType !== "Bar" ? this.seriesRef.current : this.svgRef.current).on('mousemove',(e)=>{
            e.preventDefault();
            e.stopPropagation();
        });
        select(ref).on('mousemove', function (e) {
            const leftStart = chartType === "Bar" ? 10 : proxy.margin.left + 10;
            const leftEnd = leftStart + proxy.width - 10;
            const topStart = proxy.margin.top;
            const topEnd = topStart + proxy.calcHeight;
            const [x, y] = pointer(e);
            proxy.show = true;
            if (x >= leftStart && x <= leftEnd && y >= topStart && y <= topEnd) {
                const xAxisValue = x - leftStart + 10;
                const yPos = y - topStart;
                proxy.lineMouseHover(xAxisValue, yPos, proxy.width);
            } else {
                proxy.hideTooltip();
                proxy.show = false;
            }
        }).on('mouseout', function () {
            proxy.hideTooltip();
            proxy.show = false;
        });
    }
    /**
     * Implementation of trend chart axis and series
     */
    componentDidMount() {
        this.refreshGraph();
        this.bindEvents();
        // To perform zoom in and out actions
        this.svgRef.current.addEventListener('mousewheel', this.onMouseWheel.bind(this), { passive: false });
        this.props.width.indexOf('%') !== -1 && window.addEventListener('resize', this.refreshGraph.bind(this));
    }
    componentDidUpdate() {
        this.refreshGraph();
        this.bindEvents();
    }
    componentWillUnmount() {
        this.props.width.indexOf('%') !== -1 && window.removeEventListener('resize', this.refreshGraph.bind(this));
    }
    /**
    * Method to get bar element
    */
    getBarGraph = (x: any, y: any, width: number, height: number) => {
        return 'M' + x + ',' + y + 'h' + width + 'v' + height + 'h' + (-width) + 'Z';
    }
    /**
     * Method to return calculated marker style by series marker style and global marker style
     */
    getComputerMarkerStyle = (series: ISeries, key: keyof ITrendMarker) => {
        if (isNullOrUndefined(series.markerStyle) || isNullOrUndefined(series.markerStyle[key])) {
            if (this.props.markerStyle[key] === 'SeriesColor') {
                return series.color;
            } else {
                return this.props.markerStyle[key];
            }
        } else if (key === 'fillColor' || key === 'strokeColor') {
            if (series.markerStyle[key] === 'SeriesColor') {
                return series.color;
            } else {
                return series.markerStyle[key];
            }
        } else {
            return series.markerStyle[key];
        }
    }
    /**
     * Method to find the date zoom interval
     */
    getDateZoomInterval = (startTime: Date, endTime: Date) => {
        const diff = endTime.getTime() - startTime.getTime();
        const interval = (diff / 10) / 2;
        return interval;
    }
    /**
     * Method to find the number zoom interval
     */
    getNumberZoomInterval = (start: number, end: number) => {
        const diff = end - start;
        const interval = (diff / 10) / 2;
        return interval;
    }
    /**
     * Get first point in X scale
     */
    getDefaultXPoints = () => {
        const visibleSeries = this.props.series.filter((item: any) => item.isShownInChart);
        let points: any[] = []
        if(visibleSeries && visibleSeries.length){
            visibleSeries.forEach((item)=>{
                item.points.forEach((point)=>{
                    if(!points.map((ele)=> ele.xValue).includes(point.xValue)){
                        points.push(point)
                    }
                })
            })
        }
        points = points.sort((a: any,b: any)=>{
            return parseInt(a.xValue) - parseInt(b.xValue)
        })
        return points;
    }
    /**
     * Method to get X axis label format
     * @param unit Unit of date range
     */
    getLabelFormat: () => any = () => {
        const formatMillisecond = timeFormat('.%L');
        const formatSecond = timeFormat(':%S');
        const formatMinute = timeFormat('%H:%M');
        const formatHour = timeFormat('%H:%M');
        const formatDay = timeFormat('%a %d');
        const formatWeek = timeFormat('%b %d');
        const formatMonth = timeFormat('%B');
        const formatYear = timeFormat('%Y');
        return (date: Date) => {
            return (timeSecond(date) < date ? formatMillisecond
                : timeMinute(date) < date ? formatSecond
                    : timeHour(date) < date ? formatMinute
                        : timeDay(date) < date ? formatHour
                            : timeMonth(date) < date ? (timeWeek(date) < date ? formatDay : formatWeek)
                                : timeYear(date) < date ? formatMonth
                                    : formatYear)(date);
        };
    }
    /**
     * Method to get circle element
     */
    getScatterSeries = (x: number, y: number, r: number) => {
        return 'M' + (x - r) + ',' + y + ' a' + r + ',' + r + ' 0 1, 0 ' + (r * 2) + ', 0 a ' + r + ',' + r + ' 0 1, 0 -' + (r * 2) + ', 0';
    }
    /**
     * To return the render method based on series type
     */
    getSeries = (xScale: any, yScale: any, chartType: string, seriesIndex?: number) => {        
        if (chartType === 'Line') {
            return line()
                .defined(function (d: any) { return !isNullOrUndefined(d.value); })
                .x(function (point: any) { return xScale(point.xValue); })
                // @ts-ignore
                .y(function (d) { return yScale(d.value); });
        } else if (chartType === 'Staircase-Start') {
            return line()
                .defined((d: any) => !isNullOrUndefined(d.value))
                .x(function (point: any) { return xScale(point.xValue); })
                // @ts-ignore
                .y(function (d) { return yScale(d.value); })
                .curve(curveStepAfter);
        } else if (chartType === 'Staircase-End') {
            return line()
                .defined((d: any) => !isNullOrUndefined(d.value))
                .x(function (point: any) { return xScale(point.xValue); })
                // @ts-ignore
                .y(function (d) { return yScale(d.value); })
                .curve(curveStepBefore);
        } else if (chartType === 'Area') {
            return area()
                .defined((d: any) => !isNullOrUndefined(d.value))
                .x(function (d: any) { return xScale(d.xValue); })
                .y0(this.calcHeight)
                .y1(function (d: any) { return yScale(d.value); });
        } else if (chartType === 'Bar') {
            return (data: any) => {
                const pathValue = data.map((d: any) => {
                    return isNullOrUndefined(d.value) ? '' : this.getBarGraph(xScale(d.xValue) + (seriesIndex * this.barWidth),
                        yScale(d.value),
                        this.barWidth,
                        this.calcHeight - yScale(d.value));
                });
                return pathValue.join(' ');
            };
        } else if (chartType === 'ScatterPlot') {
            return (data: any) => {
                const pathValue = data.map((d: any) => {
                    return isNullOrUndefined(d.value) ? '' : this.getScatterSeries(xScale(d.xValue), yScale(d.value), 4);
                });
                return pathValue.join(' ');
            };
        }
    }
    /**
    *
    * @param x Method to get the tooltip position based on overflown text
    * @param y
    * @param width
    * @param height
    */
    getTooltipPosition = (x: number, y: number, width: number, height: number) => {
        if ((x + width) > this.width) {
            x = x - width - 5;
        }
        if ((y + height) > this.calcHeight) {
            y = y - height - 5;
            if (y < 0) {
                y = 0;
            }
        }
        return { x, y };
    }
    /**
     * Get tooltip content value based on unit, value and status
     */
    getTooltipValue = (value: number, unit: string, status: string, customValue: string, valueFormatter: any) => {
        let newValue = '';
        if (!isNullOrUndefined(value)) {
            newValue += valueFormatter ? valueFormatter(value) : value;
        }
        if (!isNullOrUndefined(unit) && unit !== '') {
            newValue += unit;
        }
        if (!isNullOrUndefined(status) && status !== '') {
            newValue += ('-' + status);
        }
        if (!isNullOrUndefined(customValue) && customValue !== '') {
            newValue += ('-' + customValue);
        }
        return newValue;
    }
    /**
     * Get visible points array based on start and end time
     * @param points
     */
    getVisiblePoints = (points: ITrendPoint[] = []) => {
        if (this.props.xAxis.type !== 'Ordinal') {
            let sIndex = getIndexByValue(this.xScale.invert(-5), points, 'xValue', true,this.props.showTooltipOnPos);
            const eIndex = getIndexByValue(this.xScale.invert(this.width + 5), points, 'xValue', true,this.props.showTooltipOnPos);
            sIndex = sIndex > 0 ? sIndex - 1 : sIndex;
            const vPoints = points.slice(sIndex, eIndex + 1);
            return vPoints;
        } else {
            return points;
        }
    }
    /**
     * Method get currently visible series
     */
    getVisibleSeries() {
        return this.props.series.filter((item: any) => item.isShownInChart);
    }

    getYAxisGivenSeries() {
        const visibleSeries = this.getVisibleSeries();
        return visibleSeries.filter((item: any) => item.isYAxisGiven);
    }
    /**
     * Method to get tick points based on `widthPerPixel` value
     */
    getXAxisTicks() {
        const ticks: any = [];
        const points = this.getDefaultXPoints();
        const range = this.xScale.range();
        let count = parseInt(((range[1] - range[0]) / this.props.xAxis.widthPerTicks).toString());
        count = count < points.length ? parseInt((points.length / count).toString()) : 1;
        let lastTickIndex = -1;
        points.forEach(function (element: any, index: number) {
            if (index % count === 0) {
                ticks.push(element.xValue);
                lastTickIndex = index;
            }
        });
        if (points.length > 0 && points[points.length - 1].xValue !== ticks[ticks.length - 1]) {
            if ((points.length - 1 - lastTickIndex) > (count / 2)) {
                ticks.push(points[points.length - 1].xValue);
            } else {
                ticks[ticks.length - 1] = points[points.length - 1].xValue;
            }
        }
        return ticks;
    }
   
    /**
     * Get point index by mouse position in svg container
     *///Function for T value
    getXIndexByMousePos = (xPos: number ,width: number) => {
        if(!this.props.showTooltipOnPos){
            if (this.props.xAxis.type !== 'Ordinal') {
                const xValue = this.xScale.invert(xPos);
                const indices = this.props.series.map((series) => {
                    const points = series.points || [];
                    return !series.isShownInChart ? -1 : getIndexByValue(xValue, points, 'xValue', this.props.tooltipPosition === 'Left' ? false : this.props.tooltipPosition === 'Right' ? true : 'Middle',this.props.showTooltipOnPos);
                });
                return indices;
            } else {
                const domain = this.xScale.domain();
                let idx = -1;
                for (let i = 0; i <= domain.length; i++) {
                    if(this.xScale(domain[i]) === undefined){
                        idx = Number(domain.length - 1)
                        break;
                    }
                    if (this.xScale(domain[i]) > xPos) {
                        if(this.xScale(domain[i]) === width && width - 10 < xPos){
                            idx = Number(domain.length - 1)
                            break;
                        }
                        else{
                            idx = i - 1;
                            break;
                        }
                    }
                }
                const indices = this.props.series.map((series: any) => series.isShownInChart ? series.points.findIndex((el: any) => el.xValue === domain[idx]) : -1);
                return indices;
            }
        }else{
            if (this.props.xAxis.type !== 'Ordinal') {
                const xValue = this.xScale.invert(xPos);
                const indices = this.props.series.map((series) => {
                    if(series.points[series.points.length-1].xValue>xValue){
                        const points = series.points || [];
                        return !series.isShownInChart ? -1 : getIndexByValue(xValue, points, 'xValue', this.props.tooltipPosition === 'Left' ? false : this.props.tooltipPosition === 'Right' ? true : 'Middle',this.props.showTooltipOnPos);
                    }else{
                        return -1
                    }

                });
                return indices;
            } else if(this.props.chartType !== 'Bar') {      
                const domain = this.xScale.domain(); //  domain ranges from 8 to 21
                let idx = -1;
                for (let i = 0; i <= domain.length; i++) {
                    if(this.xScale(domain[i]) === undefined){
                        //idx = Number(domain.length - 1)
                        break;
                    }
                    else if(parseInt(this.xScale(domain[i]).toFixed())==parseInt(xPos.toFixed())){
                              idx = i;
                            break;
                   }
                }
                const indices = this.props.series.map((series: any) => series.isShownInChart ? series.points.findIndex((el: any) => el.xValue === domain[idx]) : -1);
                return indices
            }else{
                const domain = this.xScale.domain(); //  domain ranges from 8 to 21
                let idx = -1;
                for (let i = 0; i <= domain.length; i++) {
                    if(this.xScale(domain[i]) === undefined){
                        idx = Number(domain.length - 1)
                        break;
                    }
                    if (this.xScale(domain[i]) > xPos) {
                        if(this.xScale(domain[i]) === width && width - 10 < xPos){
                            idx = Number(domain.length - 1)
                            break;
                        }
                        else{
                           idx = i - 1;
                           break;
                        }
                    }
                } 
                const indices = this.props.series.map((series: any) => series.isShownInChart ? series.points.findIndex((el: any) => el.xValue === domain[idx]) : -1);
                return indices; 
            }
        }
       
    }
    /**
    * Method to find start and end ratio for mouse wheel zoom action
    * @param event
    * @param interval
    */
    getZoomRatio = (event: any, interval: number) => {
        const graphWidth = this.width - 10;
        let [x] = pointer(event);
        x = x - this.margin.left - 10;
        const sPercent = (x * 100) / graphWidth;
        const ePercent = ((graphWidth - x) * 100) / graphWidth;
        const sInterval = ((interval * 2) * sPercent) / 100;
        const eInterval = ((interval * 2) * ePercent) / 100;
        return { sInterval, eInterval };
    }
    /**
    * Get ratio for string value X axis
    * @param event
    * @param interval
    * @param width
    * @param start
    */
    getZoomRatioForOrdinal = (event: any, interval: number, width: number, start: number) => {
        const graphWidth = width;
        let [x] = pointer(event);
        x = (x + 10 - start) - this.margin.left - 10;
        const sPercent = (x * 100) / graphWidth;
        const ePercent = ((graphWidth - x) * 100) / graphWidth;
        const sInterval = ((interval * 2) * sPercent) / 100;
        const eInterval = ((interval * 2) * ePercent) / 100;
        return { sInterval, eInterval };
    }
    hideTooltip() {
        select(this.tooltipRef.current).style('display', 'none');
        select(this.tooltipPointerRef.current).style('display', 'none');
    }
    /**
     * Mouse hover event to show tooltip
     */
    lineMouseHover = (xPos: number, yPos: number , width: number) => {
        const visibleSeries = this.getVisibleSeries();
        if (visibleSeries.length === 0 || this.isOnZooming) {
            return;
        }
        
        const indexes = this.getXIndexByMousePos(xPos , width);//Manage these indeces
        this.indexes = indexes;
        this.xPos = xPos;
        this.yPos = yPos;
        const isTooltipShown = indexes.filter((idx: any) => idx > -1);
        isTooltipShown.length > 0 && this.showTooltip(indexes, xPos, yPos);
    }
    /**
     * Event handler for zoom end event
     */
    onBrushEnd = (event: any) => {
        const extent = event.selection;
        this.isOnZooming = false;
        if (isNullOrUndefined(extent)) {
            return;
        }
        const x = this.xScale;
        let start, end, count;
        if (this.props.xAxis.type !== 'Ordinal') {
            start = x.invert(extent[0]);
            end = x.invert(extent[1]);
            count = this.props.xAxis.type === 'DateTime' ? getDateDiffUnit(start, end, this.props.dateUnit) : 0;
        } else {
            const [start1, end1] = this.xScale.range();
            const diff = (end1 - start1) / (extent[1] - extent[0]);
            this.zoomStart = start1 - ((extent[0] - 10) * diff);
            this.zoomEnd = end1 + ((this.width - extent[1]) * diff);
            select(this.brushRef.current).call(this.brush.move, null);
            this.refreshGraph();
            return;
        }
        select(this.brushRef.current).call(this.brush.move, null);
        if (isNullOrUndefined(this.zoomStart)) {
            this.zoomStart = this.props.xAxis.start;
            this.zoomEnd = this.props.xAxis.end;
            this.zoomDateUnit = this.props.dateUnit;
            this.zoomDateUnitCount = this.props.dateUnitCount;
        }
        this.props.onDateRangeChange && this.props.onDateRangeChange(start, end, this.props.dateUnit, count, true);
    }
    /**
     * Event handler for zoom start method
     */
    onBrushStart() {
        this.isOnZooming = true;
        this.hideTooltip();
    }
    /**
     * Event handler for mouse wheel event to achieve zoom
     */
    onMouseWheel = (event: any) => {
        event.preventDefault();
        let obj;
        if (this.props.xAxis.type === 'DateTime') {
            obj = this.processTimeZoom(event);
        } else if (this.props.xAxis.type === 'Number') {
            obj = this.processNumberZoom(event);
        } else if (this.props.xAxis.type === 'Ordinal') {
            obj = this.processOrdinalZoom(event);
            this.zoomStart = obj.start;
            this.zoomEnd = obj.end;
        }
        if (isNullOrUndefined(obj)) {
            return;
        }
        this.isOnZooming = false;
        if (this.props.xAxis.type !== 'Ordinal') {
            if (isNullOrUndefined(this.zoomStart)) {
                this.zoomStart = this.props.xAxis.start;
                this.zoomEnd = this.props.xAxis.end;
                this.zoomDateUnit = this.props.dateUnit;
                this.zoomDateUnitCount = this.props.dateUnitCount;
            }
            this.props.onDateRangeChange && this.props.onDateRangeChange(obj.start, obj.end, this.props.dateUnit, obj.count, true);
        } else {
            this.refreshGraph();
        }
    }
    /**
     * Method to process invalid points
     * @param points
     * @param type
     * @param statues
     */
    processInvalidPoints = (points: ITrendPoint[], type: string, statues: string[], chartType: string) => {
        let newPoints: ITrendPoint[] = [];
        if (chartType === 'Bar' || chartType === 'ScatterPlot') {
            points.forEach(element => {
                if (statues.indexOf(element.status) === -1) {
                    newPoints.push({ ...element });
                }
            });
            return newPoints;
        }
        if (type === 'Type1') {
            points.forEach(function (element, index) {
                const prevEle = index > 0 ? points[index - 1] : null;
                if (chartType === 'Area' && (!isNullOrUndefined(prevEle) && statues.indexOf(prevEle.status) !== -1) && statues.indexOf(element.status) !== -1) {
                } else {
                    newPoints.push(element);
                    if (statues.indexOf(element.status) !== -1) {
                        newPoints.push({ ...element, value: null });
                    }
                }
            });
        } else if (type === 'Type2') {
            points.forEach(function (element, index) {
                const nxtEle = index < (points.length - 1) ? points[index + 1] : null;
                if (chartType === 'Area' && (!isNullOrUndefined(nxtEle) && statues.indexOf(nxtEle.status) !== -1) && statues.indexOf(element.status) !== -1) {
                } else {
                    if (statues.indexOf(element.status) !== -1) {
                        newPoints.push({ ...element, value: null });
                    }
                    newPoints.push(element);
                }
            });
        } else if (type === 'Type3') {
            points.forEach(function (element) {
                newPoints.push({ ...element, value: statues.indexOf(element.status) !== -1 ? null : element.value });
            });
        }
        return newPoints;
    }
    /**
     * Event handler for number zoom event
     */
    processNumberZoom = (event: any) => {
        let start: number = this.props.xAxis.start as number;
        let end: number = this.props.xAxis.end as number;
        if (event.deltaY < 0 && end < (start + 0.01)) {
            return;
        }
        const interval = this.getNumberZoomInterval(start, end);
        const { sInterval, eInterval } = this.getZoomRatio(event, interval);
        if (event.deltaY > 0) {
            start = start - sInterval;
            end = end + eInterval;
        } else {
            start = start + sInterval;
            end = end - eInterval;
        }
        const count = 0;
        return { start, end, count };
    }
    /**
     * Event handler for string zoom event
     */
    processOrdinalZoom = (event: any) => {
        let [start, end] = this.xScale.range();
        const interval = (end - start) / 20;
        const { sInterval, eInterval } = this.getZoomRatioForOrdinal(event, interval, end - start, start);
        if (event.deltaY > 0) {
            start = start + sInterval;
            end = end - eInterval;
        } else {
            start = start - sInterval;
            end = end + eInterval;
        }
        const count = 0;
        if (start > 10 || end < this.width) {
            return { start: null, end: null, count: 0 };
        } else {
            return { start, end, count };
        }
    }
    /**
     * Event handler for date time zoom event
     */
    processTimeZoom = (event: any) => {
        const startTime = new Date(this.props.xAxis.start);
        const endTime = new Date(this.props.xAxis.end);
        if (event.deltaY < 0 && (endTime.getTime() < (startTime.getTime() + 15))) {
            return;
        }
        const interval = this.getDateZoomInterval(startTime, endTime);
        const { sInterval, eInterval } = this.getZoomRatio(event, interval);
        if (event.deltaY > 0) {
            // Down
            startTime.setTime(startTime.getTime() - sInterval);
            endTime.setTime(endTime.getTime() + eInterval);
        } else {
            startTime.setTime(startTime.getTime() + sInterval);
            endTime.setTime(endTime.getTime() - eInterval);
        }
        const count = getDateDiffUnit(startTime, endTime, this.props.dateUnit);
        return { start: startTime, end: endTime, count };
    }
    /** Method to refresh the  Graph with new state */
    refreshGraph() {
        this.width = this.props.width.indexOf('%') === -1 ? parseFloat(this.props.width) : this.svgRef.current?.getBoundingClientRect().width;
        if(this.width-(this.margin.left - this.margin.right)>10){
            this.width = this.width - this.margin.left - this.margin.right;
        }else{
            this.width = 10
        }        
        this.renderXAxis();
        this.setYScales();
        this.renderYAxis();
        this.props.showHorizontalGridLines && this.renderHorizontalGridLines();
        this.props.showVerticalGridLines && this.renderVerticalGridLines();
        this.renderSeries();
        this.renderZoomBrush();
        this.updateClipPathDimension();
        // this.hideTooltip();
        this.indexes.length > 0 && this.show && this.showTooltip(this.indexes,this.xPos,this.yPos);
        this.isOnZooming = false;
    }
    /**
     * Method to refresh the x axis with new scale
     */
    refreshXAxis() {
        const xAxis = axisBottom(this.xScale);
        if (this.props.xAxis.type !== 'Ordinal') {
            xAxis.ticks(this.props.xAxisTicksCount);
        } else {
            xAxis.tickValues(this.getXAxisTicks());
        }
        xAxis.tickSizeOuter(0)
            .tickPadding(10);
        // Set default tick formatter in 24hrs format
        if (this.props.xAxis.type === 'DateTime') {
            xAxis.tickFormat(this.getLabelFormat());
        }
        if (this.props.xAxis.tickFormatter) {
            xAxis.tickFormat(this.props.xAxis.tickFormatter);
        }
        if (!this.props.xAxis.tickFormatter && this.props.xAxis.type === 'Number' && this.props.xAxis.decimalPlaces !== null && this.props.xAxis.decimalPlaces !== undefined) {
            // @ts-ignore
            xAxis.tickFormat(this.getDecimalFormatter(this.props.xAxis.decimalPlaces));
        }
        if (!this.props.xAxis.tickFormatter && this.props.xAxis.type === 'DateTime' && isNullOrUndefined(this.zoomStart) && this.props.xAxisLabelFormat !== 'auto') {
            xAxis.tickFormat(timeFormat(this.props.xAxisLabelFormat) as any);
        }
        select(this.xAxisRef.current)
            .attr('clip-path', (this.props.xAxis.type === 'Ordinal' && !isNullOrUndefined(this.zoomStart)) ? `url(#${this.clipPathId})` : '');
        select(this.xAxisRef.current).html('')
            .call(xAxis);
        // Rotate labels as per given angle
        if (!isNullOrUndefined(this.props.xAxis.tickAngle) && this.props.xAxis.tickAngle !== 0) {
            select(this.xAxisRef.current)
                .selectAll("text")
                .attr("y", 0)
                .attr("x", 7)
                .attr("dy", ".35em")
                .attr("transform", "rotate(" + this.props.xAxis.tickAngle + ")")
                .style("text-anchor", "start");
        }
    }
    /**
     * Method to render circle markers
     * @param series
     * @param xScale
     * @param yScale
     */
    renderCirclePoint = (series: ISeries, xScale: any, yScale: any, seriesIndex: number = 0) => {
        const vPoints = this.getVisiblePoints(series.points) || [];
        const radius = this.getComputerMarkerStyle(series, 'radius');
        select(this.shapeRef.current)
            .append('path')
            .attr('class', 'circle-marker')
            .attr('stroke', this.getComputerMarkerStyle(series, 'strokeColor'))
            .attr('stroke-width', this.getComputerMarkerStyle(series, 'strokeWidth'))
            .attr('fill', this.getComputerMarkerStyle(series, 'fillColor'))
            .datum(vPoints)
            .attr('d', (data) => {
                const pathValue = data.map((d) => {
                    let x = (this.props.chartType === 'Bar') ? xScale(d.xValue) : xScale(d.xValue) + (seriesIndex * this.barWidth) + (this.barWidth / 2);
                    return this.getScatterSeries(x, yScale(d.value), radius as number);
                });
                return pathValue.join(' ');
            });
    }
    /**
     * Method to render data limit line series
     */
    renderDataLimitSeries = (limits: any[], xScale: any, yScale: any, color: string) => {
        if (isNullOrUndefined(limits) || limits.length === 0) {
            return;
        }
        limits.forEach((lineItem) => {
            if (!lineItem.shownInChart) {
                return;
            }
            const points = [
                {
                    xValue: this.xScale.invert ? this.xScale.invert(-5) : this.xScale.domain()[0],
                    value: lineItem.value
                },
                {
                    xValue: this.xScale.invert ? this.xScale.invert(this.width + 15) : this.xScale.domain()[this.xScale.domain().length - 1],
                    value: lineItem.value
                }
            ];
            const d3Line = select(this.dataLimitRef.current)
                .append('path')
                .datum(lineItem.points || points);
            var pathId = "data-limitsId-" + lineItem.name;
            d3Line.attr("id", pathId);
            d3Line.attr('class', 'data-limits' + (lineItem.className ? (' ' + lineItem.className) : ''));
            d3Line.style('stroke-dasharray', lineItem.strokeDasharray || '');
            d3Line.style('fill', 'transparent');
            const lineSeries = line()
                .x(function (point: any) { return xScale(point.xValue); })
                .y(function (d: any) { return yScale(d.value); });
            if (lineItem.lineType === 'Staircase-Start') {
                lineSeries.curve(curveStepAfter);
            } else if (lineItem.lineType === 'Staircase-End') {
                lineSeries.curve(curveStepBefore);
            }
      d3Line.style("stroke", color).attr("d", lineSeries);
      if (
        this.props.showDataLimitTitle !== undefined &&
        this.props.showDataLimitTitle === true
      ) {
            const d3TextLine = select(this.dataLimitRef.current).append("text");

            d3TextLine.attr("class", "data-limits-text");

            d3TextLine.attr("id", "dataLimitsTextId-" + lineItem.name);
        d3TextLine.style("fill", color);
            d3TextLine.style("font-size", "15px");
            d3TextLine
                .append("textPath")
                .attr("href", "#" + pathId)
                .attr("startOffset", "20")
                .html(lineItem.value + " - " + lineItem.name);
      }

            if (lineItem.style) {
                Object.keys(lineItem.style).forEach((key: string) => {
                    d3Line.style(key, lineItem.style[key]);
                });
            }
        });
    }
    /**
     *
     * @param series
     * @param xScale
     * @param yScale
     */
    renderMarkers = (series: ISeries, xScale: any, yScale: any, seriesIndex?: number) => {
        if ((!isNullOrUndefined(series.markerStyle) && !isNullOrUndefined(series.markerStyle.type) && series.markerStyle.type === 'None') ||
            (!isNullOrUndefined(series.markerStyle) && isNullOrUndefined(series.markerStyle.type) && this.props.markerStyle.type === 'None')) {
            return;
        }
        const type = this.getComputerMarkerStyle(series, 'type');
        if (type === 'Square') {
            this.renderSquareMarker(series, xScale, yScale, seriesIndex);
        } else if (type === 'Circle') {
            this.renderCirclePoint(series, xScale, yScale, seriesIndex);
        }
    }
    /**
     * Method to render all types of series with markers
     */
    renderSeries() {
        select(this.shapeRef.current).html('');
        select(this.seriesRef.current).html('');
        select(this.dataLimitRef.current).html('');
        select(this.gradientRef.current).html('');
        this.barWidth = 0;
        let idx = 0;
        if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
            {           
                this.finalSeriesList.forEach((item: any, index: number) => {
                    let lineItem = item.currentItem;
                    if (!lineItem.isShownInChart) {
                        return;
                    }
                    let yscaleIndex = item.yscaleIndex;
                    const yScale = this.props.isMultipleYAxis ? this.yScales[yscaleIndex] : this.yScales[0];
                    // Render data limit series
                    this.renderDataLimitSeries(lineItem.dataLimits, this.xScale, yScale, lineItem.color);
                    if(lineItem.points?.length === 0)
                    {
                        return;
                    }
                    const chartType = lineItem.chartType || this.props.chartType;
                    if(chartType == "Bar")
                    {
                            // Remove elements with class "circle-marker" after mount
                        const elementsToRemove = document.querySelectorAll('.circle-marker');
                        elementsToRemove.forEach(element => {
                        element.parentNode?.removeChild(element);
                        });
                    }
                    const series = this.getSeries(this.xScale, yScale, chartType, idx);
                    let data = lineItem.points;
                    let sortedPoints = data.sort((a, b) => {
                        let dateA = new Date(a.xValue).getTime(); // Convert xValue of object a to timestamp
                        let dateB = new Date(b.xValue).getTime(); // Convert xValue of object b to timestamp
                        
                        // Compare dates in ascending order
                        return dateA - dateB; // Compare timestamps directly
                    });
                    let visiblePoints = this.getVisiblePoints(lineItem.points || []);
                    let unProcessedPoints = visiblePoints;
                    let isProcessed = false;
                    let d3Line;
                    let d3Line1;
                    let d3InvalidLine;
                    if(this.props.showFutureValue && this.props.xAxis.type === 'DateTime' && chartType !== 'Bar'){
                        this.renderFutureGridLine();
                        let currentPoints = visiblePoints.filter((ele)=> ele.xValue < new Date());
                        let futurePoints = visiblePoints.filter((ele)=> ele.xValue > new Date());
                        if(currentPoints.length > 0){
                            futurePoints.concat(currentPoints[currentPoints.length - 1])
                        }
                        futurePoints.sort((a:any,b:any) => b.xValue - a.xValue)
                        d3Line = select(this.seriesRef.current)
                            .append('path')
                            .datum(currentPoints);
                        d3Line1 = select(this.seriesRef.current)
                            .append('path')
                            .datum(futurePoints);
                    }
                    else{
                        if ((isNullOrUndefined(lineItem.invalidPointLineType) && this.props.invalidPointLineType !== 'None') || (!isNullOrUndefined(lineItem.invalidPointLineType) && lineItem.invalidPointLineType !== 'None')) {
                            isProcessed = true;
                            visiblePoints = this.processInvalidPoints(visiblePoints, lineItem.invalidPointLineType || this.props.invalidPointLineType, lineItem.invalidStatuses || this.props.invalidStatuses, chartType);
                        }
                        d3Line = select(this.seriesRef.current)
                            .append('path')
                            .datum(visiblePoints);
                        if (isProcessed) {
                            if (chartType === 'Bar' || chartType === 'ScatterPlot') {
                                unProcessedPoints = unProcessedPoints.filter(point => {
                                    return (lineItem.invalidStatuses || this.props.invalidStatuses || []).indexOf(point.status) !== -1;
                                });
                            }
                            d3InvalidLine = select(this.seriesRef.current)
                                .append('path')
                                .datum(unProcessedPoints)
                                .attr('class', 'invalid-point-line');
                        }
                    }
                    if(chartType !== 'Bar'){
                        if(this.props.showFutureValue && this.props.xAxis.type === 'DateTime') {
                            d3Line1.attr('class', 'line');
                            d3Line1.attr('stroke-dasharray', '2 2')
                            d3Line1.style('stroke', lineItem.color)
                            // @ts-ignore
                                .attr('d', series);
                        }
                    }
                    if (chartType === 'Line') {
                        d3Line.attr('class', 'line');
                    } else if (chartType === 'Staircase-Start') {
                        d3Line.attr('class', 'staircase-start');
                    } else if (chartType === 'Staircase-End') {
                        d3Line.attr('class', 'staircase-end');
                    } else if (chartType === 'Area') {
                        d3Line.attr('class', 'area');
                        if (isNullOrUndefined(lineItem.fillType) || lineItem.fillType === 'Solid') {
                            d3Line.attr('fill', lineItem.color).style('fill-opacity', 0.25);
                        } else if (lineItem.fillType === 'Gradient') {
                            const gradientId = this.addGradientDef(lineItem);
                            d3Line.attr('fill', 'url(#' + gradientId + ')');
                        }
                    } else if (chartType === 'Bar') {
                        if (idx === 0) {
                            this.updateBarWidth(lineItem);
                        }
                        d3Line.attr('class', 'bar')
                            .attr('fill', 'none');
                        if (this.barWidth > 1) {
                            d3Line.attr('fill', lineItem.color);
                        }
                        ++idx;
                    } else if (chartType === 'ScatterPlot') {
                        d3Line.attr('fill', lineItem.color);
                    }
                    d3Line.style('stroke', lineItem.color)
                        // @ts-ignore
                        .attr('d', series);
                    if (d3InvalidLine) {
                        d3InvalidLine.style('stroke', lineItem.color)
                            // @ts-ignore
                            .attr('d', series);
                    }
                    // Render circle shape
                    this.renderMarkers(lineItem, this.xScale, yScale, idx - 1);                    
                });
            }
            else
            {
                this.props.series.forEach((lineItem: any, index: number) => {
                if (!lineItem.isShownInChart) {
                    return;
                }
                const yScale = this.props.isMultipleYAxis ? this.yScales[index] : this.yScales[0];
                const chartType = lineItem.chartType || this.props.chartType;
                const series = this.getSeries(this.xScale, yScale, chartType, idx);
                let visiblePoints = this.getVisiblePoints(lineItem.points || []);
                let unProcessedPoints = visiblePoints;
                let isProcessed = false;
                let d3Line;
                let d3Line1;
                let d3InvalidLine;
                if(this.props.showFutureValue && this.props.xAxis.type === 'DateTime' && chartType !== 'Bar'){
                    this.renderFutureGridLine();
                    let currentPoints = visiblePoints.filter((ele)=> ele.xValue < new Date());
                    let futurePoints = visiblePoints.filter((ele)=> ele.xValue > new Date());
                    if(currentPoints.length > 0){
                        futurePoints.concat(currentPoints[currentPoints.length - 1])
                    }
                    futurePoints.sort((a:any,b:any) => b.xValue - a.xValue)
                    d3Line = select(this.seriesRef.current)
                        .append('path')
                        .datum(currentPoints);
                    d3Line1 = select(this.seriesRef.current)
                        .append('path')
                        .datum(futurePoints);
                }
                else{
                    if ((isNullOrUndefined(lineItem.invalidPointLineType) && this.props.invalidPointLineType !== 'None') || (!isNullOrUndefined(lineItem.invalidPointLineType) && lineItem.invalidPointLineType !== 'None')) {
                        isProcessed = true;
                        visiblePoints = this.processInvalidPoints(visiblePoints, lineItem.invalidPointLineType || this.props.invalidPointLineType, lineItem.invalidStatuses || this.props.invalidStatuses, chartType);
                    }
                    d3Line = select(this.seriesRef.current)
                        .append('path')
                        .datum(visiblePoints);
                    if (isProcessed) {
                        if (chartType === 'Bar' || chartType === 'ScatterPlot') {
                            unProcessedPoints = unProcessedPoints.filter(point => {
                                return (lineItem.invalidStatuses || this.props.invalidStatuses || []).indexOf(point.status) !== -1;
                            });
                        }
                        d3InvalidLine = select(this.seriesRef.current)
                            .append('path')
                            .datum(unProcessedPoints)
                            .attr('class', 'invalid-point-line');
                    }
                }
                if(chartType !== 'Bar'){
                    if(this.props.showFutureValue && this.props.xAxis.type === 'DateTime') {
                        d3Line1.attr('class', 'line');
                        d3Line1.attr('stroke-dasharray', '2 2')
                        d3Line1.style('stroke', lineItem.color)
                        // @ts-ignore
                            .attr('d', series);
                    }
                }
                if (chartType === 'Line') {
                    d3Line.attr('class', 'line');
                } else if (chartType === 'Staircase-Start') {
                    d3Line.attr('class', 'staircase-start');
                } else if (chartType === 'Staircase-End') {
                    d3Line.attr('class', 'staircase-end');
                } else if (chartType === 'Area') {
                    d3Line.attr('class', 'area');
                    if (isNullOrUndefined(lineItem.fillType) || lineItem.fillType === 'Solid') {
                        d3Line.attr('fill', lineItem.color).style('fill-opacity', 0.25);
                    } else if (lineItem.fillType === 'Gradient') {
                        const gradientId = this.addGradientDef(lineItem);
                        d3Line.attr('fill', 'url(#' + gradientId + ')');
                    }
                } else if (chartType === 'Bar') {
                    if (idx === 0) {
                        this.updateBarWidth(lineItem);
                    }
                    d3Line.attr('class', 'bar')
                        .attr('fill', 'none');
                    if (this.barWidth > 1) {
                        d3Line.attr('fill', lineItem.color);
                    }
                    ++idx;
                } else if (chartType === 'ScatterPlot') {
                    d3Line.attr('fill', lineItem.color);
                }
                d3Line.style('stroke', lineItem.color)
                    // @ts-ignore
                    .attr('d', series);
                if (d3InvalidLine) {
                    d3InvalidLine.style('stroke', lineItem.color)
                        // @ts-ignore
                        .attr('d', series);
                }
                // Render circle shape
                this.renderMarkers(lineItem, this.xScale, yScale, idx - 1);
                // Render data limit series
                this.renderDataLimitSeries(lineItem.dataLimits, this.xScale, yScale, lineItem.color);
            
            });
    }
    }
    /**
     * To render square markers on each series points
     * @param series
     * @param xScale
     * @param yScale
     */
    renderSquareMarker = (series: ISeries, xScale: any, yScale: any, seriesIndex: number) => {
        const width: number = this.getComputerMarkerStyle(series, 'width') as number;
        const height: number = this.getComputerMarkerStyle(series, 'height') as number;
        const vPoints = this.getVisiblePoints(series.points) || [];
        select(this.shapeRef.current)
            .append('path')
            .attr('class', 'square-marker')
            .attr('stroke', this.getComputerMarkerStyle(series, 'strokeColor'))
            .attr('stroke-width', this.getComputerMarkerStyle(series, 'strokeWidth'))
            .attr('fill', this.getComputerMarkerStyle(series, 'fillColor'))
            .datum(vPoints)
            .attr('d', (data) => {
                const pathValue = data.map((d) => {
                    return this.getBarGraph(xScale(d.xValue) + (seriesIndex * this.barWidth) + (this.barWidth / 2) - (width / 2),
                        yScale(d.value) - (height / 2),
                        width,
                        height
                    );
                });
                return pathValue.join(' ');
            });
    }
    /**
     * To render horizontal grid lines
     */
    renderHorizontalGridLines() {
        const yScale = scaleLinear()
            .domain([0, 10])
            .range([this.calcHeight, 0]);
        select(this.gridLinesXRef.current).html('').selectAll('line.horizontalGrid').data(this.getYAxisTicks(this.props.yAxisTicksCount, yScale)).enter()
            .append('line')
            .attr('class', 'horizontalGrid')
            .attr('x1', 10)
            .attr('x2', this.width)
            .attr('y1', function (d) { return yScale(d); })
            .attr('y2', function (d) { return yScale(d); })
            .attr('fill', 'none')
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', this.props.gridLineColor)
            .attr('stroke-width', this.props.gridLineWidth);
    }
    /**
     * To render vertical grid lines
     */
    renderVerticalGridLines() {
        const xScale = this.xScale;
        let ticks = this.xScale.ticks ? xScale.ticks(this.props.xAxisTicksCount) : this.getXAxisTicks();
        ticks.push.apply(ticks, ['TST', 'TED']);
        const proxy = this;
        select(this.gridLinesYRef.current).html('').selectAll('line.verticalGrid').data(ticks).enter()
            .append('line')
            .attr('class', 'verticalGrid')
            .attr('x1', function (d) { return d === 'TST' ? 10 : d === 'TED' ? proxy.width : xScale(d); })
            .attr('x2', function (d) { return d === 'TST' ? 10 : d === 'TED' ? proxy.width : xScale(d); })
            .attr('y1', 0)
            .attr('y2', this.calcHeight)
            .attr('fill', 'none')
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', this.props.gridLineColor)
            .attr('stroke-width', this.props.gridLineWidth);
    }
    /**
     * Method to render future value line
     */
    renderFutureGridLine(){
        let xScale = this.xScale;
        const points = this.getDefaultXPoints();
        let visiblePoints = points.map((ele) => ele.xValue)
        let currentPoints = visiblePoints.filter((ele:any)=> ele < new Date());
        let futurePoints = visiblePoints.filter((ele: any)=> ele > new Date());
        if(currentPoints.length > 0){
            futurePoints.concat(currentPoints[currentPoints.length - 1])
        }
        let futureTic = [futurePoints[0]];
        const proxy = this;
        select(this.futureGridLine.current).html('').selectAll('line.verticalGrid').data(futureTic).enter()
            .append('line')
            .attr('class', 'verticalGrid')
            .attr('x1', function (d) { return  xScale(d); })
            .attr('x2', function (d) { return  xScale(d); })
            .attr('y1', 0)
            .attr('y2', this.calcHeight)
            .attr('fill', 'none')
            .attr('shape-rendering', 'crispEdges')
            .attr('stroke', 'black')
            .attr('stroke-width', 2);
    }
    /**
     * Method to render y axis in chart
     */
    renderXAxis() {
        if (this.props.xAxis.type === 'DateTime') {
            this.xScale = scaleTime()
                .domain([this.props.xAxis.start, this.props.xAxis.end]) // input
                .range([10, this.width]);
            if (isNullOrUndefined(this.zoomStart)) {
                this.xScale.nice();
            }
        } else if (this.props.xAxis.type === 'Number') {
            this.xScale = scaleLinear()
                .domain([this.props.xAxis.start, this.props.xAxis.end]) // input
                .range([10, this.width]);
            if (isNullOrUndefined(this.zoomStart)) {
                this.xScale.nice();
            }
        } else if (this.props.xAxis.type === 'Ordinal') {
            const points = this.getDefaultXPoints();
            let width = this.props.chartType === "Bar" ? this.width - 50 : this.width - 2.2
            const stRange = isNullOrUndefined(this.zoomStart) ? 11.5 : this.zoomStart;
            const enRange = isNullOrUndefined(this.zoomStart) ? width : this.zoomEnd;
            this.xScale = scalePoint()
                .domain(points.map((item: any) => { return item.xValue.toString(); })) // input
                .range([stRange, enRange]);
        }
        this.refreshXAxis();
    }
    getDecimalFormatter(decimals: number) {
        return (value: number) => {
            return value.toFixed(decimals);
        }
    }
    /**
     * To render y axis for multiple series
     */
    renderYAxis() {        
        let leftCount = 0;
        let rightCount = 0;
        select(this.yAxisRef.current).html('');        
        
        if(this.props.isMultipleYAxis){
            if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
                {            
                    this.groupedByProperty.map((group: any, groupIndex: any) => {
                        group.map((groupItem: any, index: number) => {
                            let item = groupItem.currentItem; 
                            if(item.isYAxisGiven)
                            {  
                                const isOdd = (groupIndex + 1) % 2 === 1;
                                const xPos = isOdd ? (leftCount * -this.props.yAxisWidth) : this.width + (rightCount * this.props.yAxisWidth + 5) + 10;
                                isOdd ? ++leftCount : ++rightCount;
                                const yScale = this.yScales[groupIndex];
                                const axisPos = isOdd ? axisLeft(yScale) : axisRight(yScale);
                                const tickFormatter = (item.yAxis && item.yAxis.tickFormatter) ||
                                    ((item.yAxis && item.yAxis.decimalPlaces !== null && item.yAxis.decimalPlaces !== undefined) ? this.getDecimalFormatter(item.yAxis.decimalPlaces) : null);
                                // @ts-ignore
                                tickFormatter && axisPos.tickFormat(tickFormatter);
                                select(this.yAxisRef.current).append('g')
                                    .attr('transform', 'translate(' + xPos + ',0)')
                                    .attr('stroke-width', '1.5px')
                                    .attr('class', 'trend-y-axis')
                                    .attr('style', 'color:' + item.color + ';')
                                    .call(
                                        axisPos.tickSize(0).tickSizeOuter(7).tickPadding(10).tickValues(this.getYAxisTicks(this.props.yAxisTicksCount, yScale))
                                    );
                                }
                        });
                    })    
                }
                else
                {
                    const visibleSeries = this.props.series.filter((item) => { return item.isShownInChart; });                   
                    visibleSeries.forEach((item, index) => {
                    const isOdd = (index + 1) % 2 === 1;
                    const xPos = isOdd ? (leftCount * -this.props.yAxisWidth) : this.width + (rightCount * this.props.yAxisWidth + 5) + 10;
                    isOdd ? ++leftCount : ++rightCount;
                    const yScale = this.yScales[index];
                    const axisPos = isOdd ? axisLeft(yScale) : axisRight(yScale);
                    const tickFormatter = (item.yAxis && item.yAxis.tickFormatter) ||
                        ((item.yAxis && item.yAxis.decimalPlaces !== null && item.yAxis.decimalPlaces !== undefined) ? this.getDecimalFormatter(item.yAxis.decimalPlaces) : null);
                    // @ts-ignore
                    tickFormatter && axisPos.tickFormat(tickFormatter);
                    select(this.yAxisRef.current).append('g')
                        .attr('transform', 'translate(' + xPos + ',0)')
                        .attr('stroke-width', '1.5px')
                        .attr('class', 'trend-y-axis')
                        .attr('style', 'color:' + item.color + ';')
                        .call(
                            axisPos.tickSize(0).tickSizeOuter(7).tickPadding(10).tickValues(this.getYAxisTicks(this.props.yAxisTicksCount, yScale))
                        );
                    });
            }                     
        }
        else
            {
                const xPos = (leftCount * -this.props.yAxisWidth);
                const yScale = this.yScales[0];
                const axisPos = axisLeft(yScale);
                const tickFormatter = this.getDecimalFormatter(this.props.yAxis.decimalPlaces);
                // @ts-ignore
                tickFormatter && axisPos.tickFormat(tickFormatter);
                select(this.yAxisRef.current).append('g')
                    .attr('transform', 'translate(' + xPos + ',0)')
                    .attr('stroke-width', '1.5px')
                    .attr('class', 'trend-y-axis')
                    .attr('style', 'color:' + '#000' + ';')
                    .call(
                        axisPos.tickSize(0).tickSizeOuter(7).tickPadding(10).tickValues(this.getYAxisTicks(this.props.yAxisTicksCount, yScale))
                    );
            }
    }
    // Method to get y axis ticks
    getYAxisTicks(count: number, yScale: any) {
        const tickHeight = this.calcHeight / (count - 1);
        const ticks = [];
        let currentHeight = 0;
        // First Tick
        ticks.push(yScale.invert(currentHeight));
        // Last Tick
        ticks.push(yScale.invert(this.calcHeight));
        currentHeight += tickHeight;
        while(ticks.length <= count) {
            ticks.push(yScale.invert(currentHeight));
            currentHeight += tickHeight;
        }
        return ticks;
    }
    /**
     * Method to append zoom action from d3 library
     */
    renderZoomBrush() {
        let width = this.width
        width = width < 10 ? 10 : width
        if(typeof width === 'number'){
            this.brush = brushX().extent([[10, 0], [width, this.calcHeight]]).on('end', this.onBrushEnd.bind(this))
                .on('start', this.onBrushStart.bind(this));
            select(this.brushRef.current).call(this.brush);
        }
    }
    /**
     * Reset the zoomed values in chart and date pickers
     */
    resetZoom() {
        if (!isNullOrUndefined(this.zoomStart)) {
            const st = this.props.xAxis.type === 'DateTime' ? new Date(this.zoomStart) : this.zoomStart;
            const et = this.props.xAxis.type === 'DateTime' ? new Date(this.zoomEnd) : this.zoomEnd;
            const unit = this.zoomDateUnit;
            const count = this.zoomDateUnitCount;
            this.zoomStart = this.zoomEnd = this.zoomDateUnit = this.zoomDateUnitCount = null;
            this.props.onDateRangeChange && this.props.onDateRangeChange(st, et, unit, count, true);
        }
    }
    /**
     * Method to update the Y scale values for all series
     */
    setYScales() {
        if(this.props.isMultipleYAxis) { let visisbleSeries = this.getVisibleSeries();
            if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
            {
                visisbleSeries = this.getYAxisGivenSeries();                    
            }
            this.yScales = visisbleSeries.map((item: any) => {
                    let min, max;
                    if (!isNullOrUndefined(item.yAxis)) {
                        min = item.yAxis.min;
                        max = item.yAxis.max;
                    } else {
                        min = item.minValue;
                        max = item.maxValue;
                    }
                    return scaleLinear()
                        .domain([min, max])
                        .range([this.calcHeight, 0]);
            });  
    }
    else{
        this.yScales = [scaleLinear()
            .domain([this.props.yAxis.min, this.props.yAxis.max])
            .range([this.calcHeight, 0])];
    }
    }
    setDefaultTimeLocale() {
        timeFormatDefaultLocale(this.props.d3Locale);
        this.dateFormatter = timeFormat(this.props.tooltipFormat || (this.props.d3Locale.date + ' ' + this.props.d3Locale.time));
    }
    /**
     * this is used for show target value on tooltip
     */
    getTargetValue = (limits: any,index:any) =>{
        let items: any = '';
        limits?.forEach((data:any,i:number)=>{
            if(data?.showInToolTip){
                if(data?.points && data?.points.length){
                    items += `(${data?.name}:${data?.points[index]?.value})`
                }else {
                    items += `(${data?.name}:${data?.value})`;
                }
            }
        })
        return items;
    };
    /**
     * Method to show tooltip on specific position
     */
    showTooltip = (index: number[], xPos: number, yPos: number) => {
        const calXPos = xPos + 3;
        const calYPos = yPos + 3;
        const visibleSeries = this.getVisibleSeries();
        const pIndex: number[] = index.filter(i => i !== -1);
        // There is no visible point so no need show tooltip
        if (pIndex.length === 0) {
            return;
        }
        let pointX: number = null;
        let pointXEnd: number = null;
        if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
            {  
                this.finalSeriesList.forEach((vsitem: any, vIdx: number) => {
                    let vs = vsitem.currentItem;
                    let val = vs.points[index[vIdx]] && index[vIdx] !== -1 ? this.xScale(vs.points[index[vIdx]]?.xValue) : null;
                    let endVal = index[vIdx] !== -1 ? this.xScale(vs.points[vs.points.length - 1]?.xValue) : null;
                    if(val == undefined ) 
                        val = null;
                    if(endVal == undefined)
                        endVal = null;
                    val !== null && (pointX = pointX === null ? val : pointX > val ? val : pointX);
                    endVal !== null && (pointXEnd = pointXEnd === null ? endVal : pointXEnd < endVal ? endVal : pointXEnd);
            });
            }
            else
            {
                this.props.series.forEach((vs: any, vIdx: number) => {
                    const val = vs.points[index[vIdx]] && index[vIdx] !== -1 ? this.xScale(vs.points[index[vIdx]]?.xValue) : null;
                    const endVal = index[vIdx] !== -1 ? this.xScale(vs.points[vs.points.length - 1]?.xValue) : null;
                    val !== null && (pointX = pointX === null ? val : pointX > val ? val : pointX);
                    endVal !== null && (pointXEnd = pointXEnd === null ? endVal : pointXEnd < endVal ? endVal : pointXEnd);
                });
            }
        pointXEnd = pointXEnd < this.width ? this.width : pointXEnd;
        let tooltipHeight = 0;
        if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
            {
                let pointsSeries = this.finalSeriesList.filter((item: any) => item.currentItem.isYAxisGiven);                    
                tooltipHeight = (!this.props.showCommonXLabel ? (pIndex.length * 45) : (pointsSeries.length * 20) + 30);                
            }
            else
            {
                tooltipHeight = (!this.props.showCommonXLabel ? (pIndex.length * 45) : (this.finalSeriesList.length * 20) + 30);                
            }
        const barSeries = visibleSeries.filter((line: any) => {
            return (line.chartType === 'Bar' || (isNullOrUndefined(line.chartType) && this.props.chartType === 'Bar'));
        });
        const { x, y } = this.getTooltipPosition(calXPos, calYPos, 210, tooltipHeight);
        let pointer = `<line class='tooltip-line' x1=${xPos} y=${0} x2=${xPos} y2=${this.calcHeight} style='stroke-width:0.5px;stroke:red;'></line>`;
        let markers = '';
        let labels = '';
        let vIndex = -1;
        let barIndex = 0;
        let commonXValue;
        let tooltipLabel = '';
        const xAxisValueFormatter = (this.props.xAxis.type === 'Number' && this.props.xAxis.decimalPlaces !== null && this.props.xAxis.decimalPlaces !== undefined) ? this.getDecimalFormatter(this.props.xAxis.decimalPlaces) : null;
        if (Math.round(pointX) >= 10 || Math.round(pointX) < 10 && xPos < (pointXEnd + (barSeries.length * this.barWidth))) {
            if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
                {  
                    let pointsLineItemList = this.finalSeriesList.filter((item: any) => item.currentItem.isYAxisGiven); 
                    let newPointsIndex = index.filter((i: any) => i !== -1);               
                        pointsLineItemList.forEach((lineItem: any, idx: number) => {
                            let item = lineItem.currentItem;
                            if (!item.isShownInChart || newPointsIndex[idx] === -1 || tooltipLabel !== '') {
                                return;
                            }
                            vIndex += 1;
                            let yscaleIndex = lineItem.yscaleIndex;
                            let point = item.points[newPointsIndex[idx]];
                            const yScale = this.props.isMultipleYAxis ? this.yScales[yscaleIndex] : this.yScales[0];
                            let pointX;
                            let pointY;
                            if(point) {
                                commonXValue = point.xValue;
                                pointX = this.xScale(point.xValue);
                                pointY = yScale(point.value);
                            }
                            const valueFormatter = (item.yAxis && item.yAxis.tooltipFormatter) ||
                                ((item.yAxis && item.yAxis.decimalPlaces !== null && item.yAxis.decimalPlaces !== undefined) ? this.getDecimalFormatter(item.yAxis.decimalPlaces) : null);
                            if (item.chartType === 'Bar' || (isNullOrUndefined(item.chartType) && this.props.chartType === 'Bar')) {
                                pointX += (this.barWidth / 2) + (barIndex * this.barWidth);
                                ++barIndex;
                            }
                            markers += `<circle cx=${pointX || 0} cy=${pointY || 0} r=${8} style='fill:${item.color};fill-opacity:0.8;'></circle>`;
                            
                            const xLabel = !this.props.showCommonXLabel ? `<text fill='${item.color}' x=${x + 10} y=${vIndex === 0 ? y + 20 : y + 20 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} style='font-size:15px;'>${this.props.xAxis.tooltipFormatter ? this.props.xAxis.tooltipFormatter(point?.xValue) : this.props.xAxis.type === 'DateTime' ? this.dateFormatter(point?.xValue as Date) : xAxisValueFormatter ? xAxisValueFormatter(point?.xValue as number) : point?.xValue}</text>` : '';
                                                   
                            labels += `${xLabel}
                            <rect x=${x + 10} y=${vIndex === 0 ? (y + 30) : y + 30 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} width='10' height='10' rx='1'
                            style='stroke-width:0px;fill:${item.color};'></rect>
                            <text x=${x + 10 + 15} y=${vIndex === 0 ? (y + 40) : y + 40 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} fill='${item.color}'>
                                ${item.name} ${(this.props.syncTime && this.dateFormatter(point?.xValue as Date))  ? `(${this.dateFormatter(point?.xValue as Date)})` : ''} ${' : ' + (this.getTooltipValue(point?.value, item.unit, point?.status, point?.customValue, valueFormatter))}                   
                                ${item?.dataLimits && !this.props.removeDataLimitsFromTooltip ? this.getTargetValue(item?.dataLimits,newPointsIndex[idx]):''}</text>
                            </text>`;                          
                        });
                }
                else{
                    this.props.series.forEach((item: any, idx: number) => {
                        if (!item.isShownInChart || index[idx] === -1) {
                            return;
                        }
                        vIndex += 1;
                        let point = item.points[index[idx]];
                        const yScale = this.props.isMultipleYAxis ? this.yScales[idx] : this.yScales[0];
                        let pointX;
                        let pointY;
                        if(point) {
                            commonXValue = point.xValue;
                            pointX = this.xScale(point.xValue);
                            pointY = yScale(point.value);
                        }
                        const valueFormatter = (item.yAxis && item.yAxis.tooltipFormatter) ||
                            ((item.yAxis && item.yAxis.decimalPlaces !== null && item.yAxis.decimalPlaces !== undefined) ? this.getDecimalFormatter(item.yAxis.decimalPlaces) : null);
                        if (item.chartType === 'Bar' || (isNullOrUndefined(item.chartType) && this.props.chartType === 'Bar')) {
                            pointX += (this.barWidth / 2) + (barIndex * this.barWidth);
                            ++barIndex;
                        }
                        markers += `<circle cx=${pointX || 0} cy=${pointY || 0} r=${8} style='fill:${item.color};fill-opacity:0.8;'></circle>`;
                        const xLabel = !this.props.showCommonXLabel ? `<text fill='${item.color}' x=${x + 10} y=${vIndex === 0 ? y + 20 : y + 20 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} style='font-size:15px;'>${this.props.xAxis.tooltipFormatter ? this.props.xAxis.tooltipFormatter(point?.xValue) : this.props.xAxis.type === 'DateTime' ? this.dateFormatter(point?.xValue as Date) : xAxisValueFormatter ? xAxisValueFormatter(point?.xValue as number) : point?.xValue}</text>` : '';
                        labels += `${xLabel}
                        <rect x=${x + 10} y=${vIndex === 0 ? (y + 30) : y + 30 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} width='10' height='10' rx='1'
                        style='stroke-width:0px;fill:${item.color};'></rect>
                        <text x=${x + 10 + 15} y=${vIndex === 0 ? (y + 40) : y + 40 + (vIndex * (this.props.showCommonXLabel ? 20 : 40))} fill='${item.color}'>
                            ${item.name} ${(this.props.syncTime && this.dateFormatter(point?.xValue as Date))  ? `(${this.dateFormatter(point?.xValue as Date)})` : ''} ${' : ' + (this.getTooltipValue(point?.value, item.unit, point?.status, point?.customValue, valueFormatter))}                    
                            ${item?.dataLimits ? this.getTargetValue(item?.dataLimits,index[idx]):''}</text>
                        </text>`;
                    });
                }
            let element = `<rect class='tooltip-cont' x=${x} y=${y} width='210' height='${tooltipHeight}' rx='2'
                style='stroke-width:1px;stroke:#bababa;fill:white;fill-opacity:1;'>
            </rect>`;
            const xLabel = this.props.showCommonXLabel ? `<text x=${x + 10} y=${y + 20} style='font-size:15px;'>
                     ${this.props.xAxis.tooltipFormatter ? this.props.xAxis.tooltipFormatter(commonXValue) : this.props.xAxis.type === 'DateTime' ? this.dateFormatter(commonXValue) : xAxisValueFormatter ? xAxisValueFormatter(commonXValue as number) : commonXValue}
                     </text>` : '';            
                
            element = element + xLabel + labels;        
            select(this.tooltipRef.current).style('display', null)
                .html(element);
            this.updateTooltipPosition(xPos, y, tooltipHeight, x);
        } else {
            this.hideTooltip();
        }
        pointer += markers;
        select(this.tooltipPointerRef.current).style('display', null)
            .html(pointer);
    }
    /**
     * Method calculate the bar width for bar chart type based on available with
     */
    updateBarWidth = (lineItem: ISeries) => {
        const visibleSeries = this.getVisibleSeries();
        let width = this.width;
        let sIndex;
        let eIndex;
        if (this.props.xAxis.type !== 'Ordinal') {
            sIndex = getIndexByValue(this.props.xAxis.start, lineItem.points, 'xValue', false,this.props.showTooltipOnPos);
            eIndex = getIndexByValue(this.props.xAxis.end, lineItem.points, 'xValue', false,this.props.showTooltipOnPos);
        } else {
            sIndex = 0;
            eIndex = lineItem.points.length;
            const range = this.xScale.range();
            width = range[1] - range[0];
        }
        width = width / (eIndex - sIndex);
        width = width / visibleSeries.length;
        width = width > 8 ? 8 : width;
        this.barWidth = width;
    }
    updateClipPathDimension() {
        let width = this.width;
        width = width < 8 ? 8 : width
        select(this.clipPathRef.current)
            .attr('width',width - 8)
            .attr('height', this.calcHeight + 10);
    }
    /**
     * Update tooltip position based on its content
     */
    updateTooltipPosition = (x: number, y: number, height: number, modifiedX: number) => {
        const width = this.tooltipRef.current.getBBox().width;
        if (width > 210) {
            const newPos = this.getTooltipPosition(x, y, width + 10, height);
            select(this.tooltipRef.current)
                .select('.tooltip-cont')
                .attr('width', width + 10);
            if (x !== newPos.x) {
                const children = this.tooltipRef.current.children;
                for (let i = 0; i < children.length; i++) {
                    const ele = children[i];
                    select(ele).attr('x', ele.getAttribute('x') - Math.abs(modifiedX - newPos.x));
                }
            }
        }
    }
    render() {
        let visibleSeries = this.props.series.filter((item) => { return item.isShownInChart});
        if(this.props.customizedMultipleYAxis !== undefined && this.props.customizedMultipleYAxis === true)
            {
                visibleSeries = this.props.series.filter((item) => { return item.isShownInChart && item.isYAxisGiven; });
            }
        let translateX = parseInt((visibleSeries.length / 2).toString()) + visibleSeries.length % 2;
        const { margin } = this;
        if(this.props.isMultipleYAxis){
            translateX = translateX * (this.props.yAxisWidth + 5);
            margin.left = translateX;
            margin.right = (parseInt((visibleSeries.length / 2).toString()) * (this.props.yAxisWidth + 5)) + 10;
        } else {
            translateX = parseInt((1 / 2).toString()) + (this.props.yAxisWidth + 10);
            margin.left = translateX;
            margin.right = (parseInt((1 / 2).toString()) * (this.props.yAxisWidth + 5)) + 25;
        }
        this.calcHeight = this.props.height - margin.top - margin.bottom;
        
        let visibleSeriesList = this.getVisibleSeries();
          // Step 1: Group the data by 'name'
        const groupedDataByProperty = visibleSeriesList.reduce((acc: any, currentItem: any) => {
            const { name, id } = currentItem;
            
            // Check if there's already an array for the current 'name', if not, create one
            if (!acc[name]) {
            acc[name] = [];
            }      
            // Push the current item to the corresponding 'name' array
            acc[name].push({ name, id, currentItem });
            
            return acc;
        }, {});
  
      // Step 2: Convert groupedData object into an array of arrays (one array per group)      
      this.groupedByProperty = Object.values(groupedDataByProperty);      
      console.log(this.groupedByProperty);

      this.finalSeriesList = [];
      this.groupedByProperty.map((group: any, groupIndex: any) => {            
            group.map((item: any, itemIndex: any) => { 
                item.yscaleIndex = groupIndex;
                this.finalSeriesList.push(item);  
            })
        })

        console.log("Final series list");
        console.log(this.finalSeriesList);
        return (
            <svg ref={this.svgRef} width={this.props.width.indexOf('%') !== -1 ? '100%' : this.props.width} height={this.props.height} onDoubleClick={this.resetZoom.bind(this)}>
                {this.props.backgroundImage && <image className='bg-img' width='100%' height='100%' href={this.props.backgroundImage} />}
                {this.props.backgroundColor && <rect className='bg-color' width='100%' height='100%' fill={this.props.backgroundColor} />}
                <defs ref={this.gradientRef} />
                <defs>
                    <clipPath id={this.clipPathId}>
                        <rect ref={this.clipPathRef} x='9' y='-5' />
                    </clipPath>
                </defs>
                <g transform={'translate(' + translateX + ',' + margin.top + ')'}>
                    <g
                        className='trend-x-axis' ref={this.xAxisRef} clipPath={(this.props.xAxis.type === 'Ordinal' && !isNullOrUndefined(this.zoomStart)) ? `url(#${this.clipPathId})` : ''}
                        strokeWidth='3px' transform={'translate(0,' + (this.props.height - margin.top - margin.bottom) + ')'}
                    />
                    <g ref={this.yAxisRef} />
                    <g ref={this.gridLinesXRef} clipPath={`url(#${this.clipPathId})`} />
                    <g ref={this.gridLinesYRef} clipPath={`url(#${this.clipPathId})`} />
                    <g ref={this.tooltipPointerRef} style={{ display: 'none' }} clipPath={`url(#${this.clipPathId})`} />
                    <g ref={this.futureGridLine}  clipPath={`url(#${this.clipPathId})`} />
                    <g className='brush' ref={this.brushRef} />
                    <g className='series-container' ref={this.seriesRef} clipPath={`url(#${this.clipPathId})`} />
                    <g className='data-limit-container' ref={this.dataLimitRef} clipPath={`url(#${this.clipPathId})`} />
                    <g className='shape-container' ref={this.shapeRef} clipPath={`url(#${this.clipPathId})`} />
                    <g className='tooltip-container' ref={this.tooltipRef} style={{ display: 'none' }} clipPath={`url(#${this.clipPathId})`} />
                </g>
            </svg>
        );
    }
}
